import React from 'react';
import { Forms } from '@ofself/shell';

const WEBHOOK_EVENTS = ['node.created', 'node.updated', 'node.deleted', 'node.embedding.updated', 'node.*'];

/**
 * Reusable webhook URL + events selector.
 * Used by DeveloperDashboard (register) and MyAppsTab (edit).
 *
 * Props:
 *   webhookUrl: string
 *   onWebhookUrlChange: (url: string) => void
 *   selectedEvents: string[]
 *   onEventsChange: (events: string[]) => void
 *   block: emit the kit's form-folio cells instead of this component's own
 *     markup — the URL as a real Forms.Field and the events as their own
 *     block. Opt-in for the same reason FormGroup's is: MyAppsTab edits an
 *     app inside a panel, where a page-scale cell would be wrong.
 */
function WebhookEventsSelector({ webhookUrl, onWebhookUrlChange, selectedEvents = [], onEventsChange, block = false }) {
  const toggleEvent = (event, checked) => {
    const updated = checked
      ? [...selectedEvents, event]
      : selectedEvents.filter(ev => ev !== event);
    onEventsChange(updated);
  };

  const events = WEBHOOK_EVENTS.map(event => (
    <label key={event} className="webhookevents__event">
      <input
        type="checkbox"
        checked={selectedEvents.includes(event)}
        onChange={(e) => toggleEvent(event, e.target.checked)}
      />
      <span>{event}</span>
      {event === 'node.*' && <span className="webhookevents__all">(all)</span>}
    </label>
  ));

  if (block) {
    return (
      <>
        <Forms.Field
          label="Webhook URL"
          htmlFor="webhook-url"
          hint="Where ofSelf POSTs when the events below fire. Leave blank for none."
        >
          <input
            id="webhook-url"
            type="url"
            value={webhookUrl || ''}
            onChange={(e) => onWebhookUrlChange(e.target.value)}
            placeholder="https://yourapp.com/webhook"
          />
        </Forms.Field>
        {webhookUrl && (
          <Forms.Block className="webhookevents__cell">
            {/* A checkbox group is not a single control, so it is a block of
                its own rather than a field pretending to be one. */}
            <p className="webhookevents__label">Events</p>
            <div className="webhookevents__events">{events}</div>
          </Forms.Block>
        )}
      </>
    );
  }

  return (
    <>
      <div style={{ marginBottom: '12px' }}>
        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '4px' }}>
          Webhook URL
        </label>
        <input
          type="url"
          value={webhookUrl || ''}
          onChange={(e) => onWebhookUrlChange(e.target.value)}
          placeholder="https://yourapp.com/webhook"
          style={{
            width: '100%', padding: '8px', borderRadius: '6px',
            border: '1px solid var(--border-color)', fontSize: '13px',
            background: 'var(--bg-primary)', color: 'var(--text-primary)',
            boxSizing: 'border-box',
          }}
        />
      </div>
      {webhookUrl && (
        <div>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '4px' }}>
            Events
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {WEBHOOK_EVENTS.map(event => (
              <label key={event} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={selectedEvents.includes(event)}
                  onChange={(e) => toggleEvent(event, e.target.checked)}
                />
                <span>{event}</span>
                {event === 'node.*' && <span style={{ color: 'var(--text-tertiary)', fontSize: '11px' }}>(all)</span>}
              </label>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

export default WebhookEventsSelector;
